import { getOffMarketDocuments } from "@/lib/admin.functions";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  FileJson,
  FileText,
  Filter,
  LogOut,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  MessageSquareText,
  Star,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PublishPreview } from "@/components/admin/publish-preview";
import { JobDraftEditor } from "@/components/admin/job-draft-editor";
import { JobContactReports } from "@/components/admin/job-contact-reports";
import { EntityDraftEditor } from "@/components/admin/entity-draft-editor";
import {
  adminLogin,
  adminLogout,
  adminSessionStatus,
  approveSubmission,
  deleteManagedSubmission,
  getSubmissionReview,
  listSubmissions,
  listInternalCustomerFeedback,
  listInternalProviderFeedback,
  reactivateSubmission,
  rejectSubmission,
  requestAdminPasswordReset,
  resetAdminPassword,
  adminResetTokenStatus,
  unpublishSubmission,
  setSubmissionStatus,
} from "@/lib/admin.functions";
import type { AdminResetTokenStatus } from "@/lib/admin.functions";
import {
  PasswordChecklist,
  formatDuration,
  isPasswordValid,
} from "@/components/admin/password-checklist";

import type { SubmissionRow } from "@/lib/submissions.types";
import type { AdminCustomerFeedback } from "@/lib/customer-account.functions";
import type { AdminProviderInternalFeedback } from "@/lib/provider-feedback.functions";
import { normalizeProviderType } from "@/lib/provider-types";

export const Route = createFileRoute("/admin")({
  staticData: { sitemap: false, toasts: true },
  ssr: false,
  component: AdminPage,
  head: () => ({
    meta: [
      { title: "Begner Admin. Formularanfragen verwalten" },
      {
        name: "description",
        content:
          "Interner Bereich von Begner zur Verwaltung aller Formularanfragen mit Filter, Statusverwaltung und CSV-Export.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Begner Admin" },
      { property: "og:description", content: "Interner Bereich für Formularanfragen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const FORM_LABELS: Record<string, string> = {
  auftrag: "Auftrag einstellen",
  registrierung: "Anbieterprofil",
  konto_registrierung: "Registrierung",
  immobilie: "Immo-Inserat",
  objektanfrage: "Objektanfrage",
  off_market: "Diskreter Immobilienverkauf",
};

const ADMIN_GROUPS = [
  { value: "alle", label: "Alle", formType: "alle", providerType: "alle" },
  { value: "immobilie", label: "Immo-Inserate", formType: "immobilie", providerType: "alle" },
  {
    value: "makler",
    label: "Makler werden",
    formType: "registrierung",
    providerType: "immobilienmakler",
  },
  {
    value: "auftragnehmer",
    label: "Auftragnehmer",
    formType: "registrierung",
    providerType: "auftragnehmer",
  },
  { value: "auftrag", label: "Auftrag einstellen", formType: "auftrag", providerType: "alle" },
  {
    value: "konto",
    label: "Registrierung",
    formType: "konto_registrierung",
    providerType: "alle",
  },
] as const;

const STATUS_LABELS: Record<string, string> = {
  neu: "Neu",
  in_bearbeitung: "In Bearbeitung",
  erledigt: "Erledigt",
  archiviert: "Archiviert",
  freigegeben: "Freigegeben",
  abgelehnt: "Abgelehnt",
};

const PROVIDER_TYPE_LABELS = {
  auftragnehmer: "Bau-Auftragnehmer",
  immobilienmakler: "Immobilienmakler",
} as const;

function providerTypeLabel(row: SubmissionRow) {
  if (row.form_type !== "registrierung" || !row.entity || Array.isArray(row.entity)) return null;
  const providerType = normalizeProviderType(row.entity["type"]);
  return providerType ? PROVIDER_TYPE_LABELS[providerType] : null;
}

function AdminPage() {
  const checkSession = useServerFn(adminSessionStatus);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    checkSession()
      .then((res) => setAuthenticated(res.authenticated))
      .catch(() => setAuthenticated(false));
  }, [checkSession]);

  if (authenticated === null) {
    return (
      <div className="container-page flex min-h-[60vh] items-center justify-center py-20 text-muted-foreground">
        Lade Adminbereich.
      </div>
    );
  }

  return authenticated ? (
    <Dashboard onLogout={() => setAuthenticated(false)} />
  ) : (
    <LoginView onSuccess={() => setAuthenticated(true)} />
  );
}

function useCountdown(target: string | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target]);
  if (!target) return null;
  const remaining = new Date(target).getTime() - now;
  return { remaining, expired: remaining <= 0, label: formatDuration(remaining) };
}

function LoginView({ onSuccess }: { onSuccess: () => void }) {
  const login = useServerFn(adminLogin);
  const requestReset = useServerFn(requestAdminPasswordReset);
  const resetPassword = useServerFn(resetAdminPassword);
  const tokenStatus = useServerFn(adminResetTokenStatus);

  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [tokenState, setTokenState] = useState<AdminResetTokenStatus | null>(null);
  const [tokenChecked, setTokenChecked] = useState(false);

  const lock = useCountdown(lockedUntil);
  const tokenCountdown = useCountdown(tokenState?.status === "valid" ? tokenState.expiresAt : null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("reset");
    if (token) setResetToken(token);
  }, []);

  useEffect(() => {
    if (!resetToken) return;
    let active = true;
    tokenStatus({ data: { token: resetToken } })
      .then((res) => {
        if (active) setTokenState(res);
      })
      .catch(() => {
        if (active) setTokenState(null);
      })
      .finally(() => {
        if (active) setTokenChecked(true);
      });
    return () => {
      active = false;
    };
  }, [resetToken, tokenStatus]);

  const backToLogin = () => {
    window.history.replaceState(null, "", "/admin");
    setResetToken(null);
    setTokenState(null);
    setTokenChecked(false);
    setError(null);
  };

  if (resetToken) {
    const linkExpired =
      tokenState?.status === "expired" ||
      (tokenState?.status === "valid" && tokenCountdown?.expired);
    const linkInvalid = tokenState?.status === "invalid";
    const canSet = tokenState?.status === "valid" && !tokenCountdown?.expired;

    return (
      <div className="container-page flex min-h-[70vh] items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-2">
            <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="size-5" />
            </div>
            <CardTitle className="text-2xl">Neues Adminpasswort</CardTitle>
            <p className="text-sm text-muted-foreground">
              Mindestens 10 Zeichen mit Gross- und Kleinbuchstaben sowie einer Ziffer.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {!tokenChecked ? (
              <p className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                Reset-Link wird geprüft.
              </p>
            ) : linkInvalid ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Dieser Reset-Link ist ungültig oder wurde bereits verwendet. Bitte über „Passwort
                vergessen“ einen neuen Link anfordern.
              </p>
            ) : linkExpired ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Dieser Reset-Link ist abgelaufen. Bitte über „Passwort vergessen“ einen neuen Link
                anfordern.
              </p>
            ) : (
              <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
                Link noch gültig für {tokenCountdown?.label ?? "."}
              </p>
            )}
            <form
              className="space-y-4"
              onSubmit={async (event) => {
                event.preventDefault();
                setError(null);
                if (!isPasswordValid(newPassword)) {
                  setError("Bitte alle Passwortanforderungen erfüllen.");
                  return;
                }
                if (newPassword !== repeatPassword) {
                  setError("Die beiden Passwörter stimmen nicht überein.");
                  return;
                }
                setPending(true);
                try {
                  const res = await resetPassword({
                    data: { token: resetToken, password: newPassword },
                  });
                  if (res.ok) {
                    toast.success(res.message);
                    window.history.replaceState(null, "", "/admin");
                    onSuccess();
                  } else {
                    setError(res.message);
                  }
                } catch {
                  setError("Zurücksetzen fehlgeschlagen. Bitte den Link neu anfordern.");
                } finally {
                  setPending(false);
                }
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort *</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={!canSet}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeat-password">Passwort wiederholen *</Label>
                <Input
                  id="repeat-password"
                  type="password"
                  autoComplete="new-password"
                  value={repeatPassword}
                  onChange={(event) => setRepeatPassword(event.target.value)}
                  disabled={!canSet}
                  required
                />
              </div>
              <PasswordChecklist password={newPassword} repeat={repeatPassword} />
              {error ? (
                <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full"
                disabled={pending || !canSet || !isPasswordValid(newPassword)}
              >
                {pending ? "Speichern." : "Passwort speichern"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={backToLogin}>
                Zurück zum Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-page flex min-h-[70vh] items-center justify-center py-16">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="size-5" />
          </div>
          <CardTitle className="text-2xl">Begner Adminbereich</CardTitle>
          <p className="text-sm text-muted-foreground">
            Bitte Adminpasswort eingeben, um alle Formularanfragen zu sehen.
          </p>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault();
              setPending(true);
              setError(null);
              setInfo(null);
              try {
                const res = await login({ data: { password } });
                if (res.ok) {
                  setLockedUntil(null);
                  onSuccess();
                } else {
                  setLockedUntil(res.reason === "rate_limited" ? (res.retryAt ?? null) : null);
                  setError(res.message);
                  toast.error(res.message);
                }
              } catch (error) {
                const status =
                  error instanceof Response
                    ? error.status
                    : ((error as { status?: number } | null)?.status ?? 0);
                const message =
                  status === 429
                    ? "Zu viele Loginversuche. Bitte später erneut versuchen."
                    : "Der Server konnte den Login nicht prüfen. Bitte später erneut versuchen.";
                setError(message);
                toast.error(message);
              } finally {
                setPending(false);
              }
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="admin-password">Passwort *</Label>
              <Input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            {lock && !lock.expired ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Login gesperrt. Nächster Versuch möglich in {lock.label} (ab{" "}
                {new Date(lockedUntil!).toLocaleTimeString("de-CH", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {" Uhr)."}
              </p>
            ) : lock?.expired ? (
              <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
                Die Sperre ist abgelaufen. Sie können es erneut versuchen.
              </p>
            ) : error ? (
              <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}
            {info ? (
              <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">{info}</p>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              disabled={pending || !password || Boolean(lock && !lock.expired)}
            >
              {pending ? "Anmelden." : "Anmelden"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={pending}
              onClick={async () => {
                setPending(true);
                setError(null);
                setInfo(null);
                try {
                  const res = await requestReset();
                  if (res.ok) {
                    setInfo(res.message);
                    toast.success(res.message);
                  } else {
                    setError(res.message);
                  }
                } catch {
                  setError("E-Mail konnte nicht gesendet werden. Bitte später erneut versuchen.");
                } finally {
                  setPending(false);
                }
              }}
            >
              Passwort vergessen
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerFeedbackOverview({ feedback }: { feedback: AdminCustomerFeedback[] }) {
  const average = feedback.length
    ? feedback.reduce((sum, item) => sum + item.rating, 0) / feedback.length
    : 0;
  return (
    <Card className="overflow-hidden border-primary/15">
      <div className="h-1.5 bg-gradient-brand" />
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <MessageSquareText className="size-5 text-primary" />
            Internes Kundenfeedback
          </span>
          <span className="flex items-center gap-1.5 text-base">
            <Star className="size-4 fill-amber-400 text-amber-400" />
            {feedback.length ? average.toFixed(1) : "–"} · {feedback.length} Bewertungen
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch kein Kundenfeedback vorhanden.</p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {feedback.slice(0, 8).map((item) => (
              <div key={item.id} className="rounded-xl border border-border/70 bg-muted/25 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{item.job_title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[item.reference, item.customer_email].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-bold">
                    <Star className="size-4 fill-amber-400 text-amber-400" /> {item.rating}/5
                  </span>
                </div>
                {item.comment ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground/80">
                    {item.comment}
                  </p>
                ) : null}
                <p className="mt-3 text-xs text-muted-foreground">
                  {new Date(item.updated_at).toLocaleString("de-CH")}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProviderFeedbackOverview({ feedback }: { feedback: AdminProviderInternalFeedback[] }) {
  const average = feedback.length
    ? feedback.reduce((sum, item) => sum + item.rating, 0) / feedback.length
    : 0;
  return (
    <Card className="overflow-hidden border-primary/15">
      <div className="h-1.5 bg-gradient-brand" />
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <MessageSquareText className="size-5 text-primary" />
            Internes Auftragnehmerfeedback
          </span>
          <span className="flex items-center gap-1.5 text-base">
            <Star className="size-4 fill-amber-400 text-amber-400" />
            {feedback.length ? average.toFixed(1) : "–"} · {feedback.length} Bewertungen
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch kein Auftragnehmerfeedback vorhanden.
          </p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {feedback.slice(0, 8).map((item) => (
              <div key={item.id} className="rounded-xl border border-border/70 bg-muted/25 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold">{item.provider_company || item.provider_name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[item.provider_company ? item.provider_name : null, item.provider_email]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-bold">
                    <Star className="size-4 fill-amber-400 text-amber-400" /> {item.rating}/5
                  </span>
                </div>
                {item.comment ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground/80">
                    {item.comment}
                  </p>
                ) : null}
                <p className="mt-3 text-xs text-muted-foreground">
                  {new Date(item.updated_at).toLocaleString("de-CH")}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Dashboard({ onLogout }: { onLogout: () => void }) {
  const fetchRows = useServerFn(listSubmissions);
  const fetchFeedback = useServerFn(listInternalCustomerFeedback);
  const fetchProviderFeedback = useServerFn(listInternalProviderFeedback);
  const changeStatus = useServerFn(setSubmissionStatus);
  const logout = useServerFn(adminLogout);

  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [feedback, setFeedback] = useState<AdminCustomerFeedback[]>([]);
  const [providerFeedback, setProviderFeedback] = useState<AdminProviderInternalFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [formType, setFormType] = useState("alle");
  const [providerType, setProviderType] = useState("alle");
  const [status, setStatus] = useState("alle");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detail, setDetail] = useState<SubmissionRow | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const load = useMemo(
    () => async () => {
      setLoading(true);
      try {
        const [res, feedbackResult, providerFeedbackResult] = await Promise.all([
          fetchRows({
            data: {
              formType,
              providerType: providerType as "alle" | "auftragnehmer" | "immobilienmakler",
              status,
              search: debouncedSearch || undefined,
              from: from ? new Date(from).toISOString() : undefined,
              to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
            },
          }),
          fetchFeedback(),
          fetchProviderFeedback(),
        ]);
        setRows(res.rows);
        setFeedback(feedbackResult.feedback);
        setProviderFeedback(providerFeedbackResult.feedback);
      } catch {
        toast.error("Anfragen konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    },
    [
      fetchFeedback,
      fetchProviderFeedback,
      fetchRows,
      formType,
      providerType,
      status,
      debouncedSearch,
      from,
      to,
    ],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Sofortige clientseitige Suche, während die Serverabfrage noch läuft.
  const term = search.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!term) return rows;
    return rows.filter((row) =>
      [row.reference, row.headline, row.customer_name ?? "", row.customer_email]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [rows, term]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredRows, currentPage, pageSize],
  );

  useEffect(() => {
    setPage(1);
  }, [formType, providerType, status, term, from, to, pageSize]);

  const activeGroup =
    ADMIN_GROUPS.find((group) => group.formType === formType && group.providerType === providerType)
      ?.value ?? "alle";

  const exportCsv = () => {
    const header = [
      "Referenz",
      "Datum",
      "Formular",
      "Titel",
      "Name",
      "E-Mail",
      "Sprache",
      "Status",
      "Details",
    ];
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const lines = filteredRows.map((row) =>
      [
        row.reference,
        new Date(row.created_at).toLocaleString("de-CH"),
        FORM_LABELS[row.form_type] ?? row.form_type,
        row.headline,
        row.customer_name ?? "",
        row.customer_email,
        row.locale.toUpperCase(),
        STATUS_LABELS[row.status] ?? row.status,
        (row.payload ?? [])
          .map(
            (section) =>
              `${section.title}: ${section.fields.map((f) => `${f.label}=${f.value}`).join(" | ")}`,
          )
          .join(" || "),
      ]
        .map((value) => escape(String(value)))
        .join(";"),
    );
    const csv = `\uFEFF${[header.join(";"), ...lines].join("\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `begner-anfragen-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-page space-y-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Formularanfragen</h1>
          <p className="text-sm text-muted-foreground">
            {loading ? "Lade Anfragen." : `${filteredRows.length} Anfragen gefunden.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <a href="/admin-rabattcodes">Rabattcodes</a>
          </Button>
          <Button asChild variant="outline">
            <a href="/admin-rechnungen">
              <FileText className="size-4" /> Rechnungen
            </a>
          </Button>
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="size-4" /> Aktualisieren
          </Button>
          <Button onClick={exportCsv} disabled={!filteredRows.length}>
            <Download className="size-4" /> CSV Export
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              await logout();
              onLogout();
            }}
          >
            <LogOut className="size-4" /> Abmelden
          </Button>
        </div>
      </div>

      <CustomerFeedbackOverview feedback={feedback} />
      <ProviderFeedbackOverview feedback={providerFeedback} />
      <JobContactReports />

      <Tabs
        value={activeGroup}
        onValueChange={(value) => {
          const group = ADMIN_GROUPS.find((item) => item.value === value);
          if (!group) return;
          setFormType(group.formType);
          setProviderType(group.providerType);
        }}
      >
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-3 xl:grid-cols-6">
          {ADMIN_GROUPS.map((group) => (
            <TabsTrigger
              key={group.value}
              value={group.value}
              className="min-h-10 whitespace-normal px-3 py-2"
            >
              {group.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="size-4 text-primary" /> Filter
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-6">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="search">Suche</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="search"
                className="pl-9"
                placeholder="Referenz, Name, E-Mail"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Formular</Label>
            <Select
              value={formType}
              onValueChange={(value) => {
                setFormType(value);
                if (value !== "registrierung") setProviderType("alle");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Formulare</SelectItem>
                {Object.entries(FORM_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Status</SelectItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Anbieterart</Label>
            <Select
              value={providerType}
              onValueChange={(value) => {
                setProviderType(value);
                if (value !== "alle") setFormType("registrierung");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Anbieterarten</SelectItem>
                {Object.entries(PROVIDER_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label htmlFor="from">Von</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="to">Bis</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referenz</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Formular</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    Keine Anfragen gefunden.
                  </TableCell>
                </TableRow>
              ) : null}
              {pagedRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.reference}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(row.created_at).toLocaleString("de-CH")}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="secondary">
                        {FORM_LABELS[row.form_type] ?? row.form_type}
                      </Badge>
                      {providerTypeLabel(row) ? (
                        <Badge variant="outline">{providerTypeLabel(row)}</Badge>
                      ) : null}
                      {row.form_type === "konto_registrierung" ? (
                        <Badge variant={row.email_verified_at ? "default" : "outline"}>
                          {row.email_verified_at ? "E-Mail bestätigt" : "E-Mail offen"}
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{row.customer_name || "."}</div>
                    <a className="text-xs text-primary" href={`mailto:${row.customer_email}`}>
                      {row.customer_email}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={row.status}
                      disabled={
                        ((Boolean(row.published_id) || row.form_type === "konto_registrierung") &&
                          row.status === "freigegeben") ||
                        ((row.form_type === "registrierung" ||
                          row.form_type === "konto_registrierung") &&
                          (row.status === "abgelehnt" ||
                            ((Boolean(row.published_id) ||
                              row.form_type === "konto_registrierung") &&
                              row.status !== "freigegeben")))
                      }
                      onValueChange={async (value) => {
                        setRows((prev) =>
                          prev.map((item) =>
                            item.id === row.id ? { ...item, status: value } : item,
                          ),
                        );
                        try {
                          await changeStatus({ data: { id: row.id, status: value as "neu" } });
                        } catch {
                          toast.error("Status konnte nicht gespeichert werden.");
                          void load();
                        }
                      }}
                    >
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                          <SelectItem
                            key={value}
                            value={value}
                            disabled={value === "freigegeben" || value === "abgelehnt"}
                          >
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(row)}>
                      Ansehen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Pro Seite</span>
          <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="w-[90px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100, 200].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>
            {filteredRows.length === 0
              ? "Keine Einträge"
              : `${(currentPage - 1) * pageSize + 1} bis ${Math.min(currentPage * pageSize, filteredRows.length)} von ${filteredRows.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setPage(currentPage - 1)}
          >
            <ChevronLeft className="size-4" /> Zurück
          </Button>
          <span className="text-sm text-muted-foreground">
            Seite {currentPage} von {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            Weiter <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] min-w-0 grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="shrink-0 border-b border-border px-6 pb-4 pt-6 pr-12">
            <DialogTitle>{detail?.headline}</DialogTitle>
          </DialogHeader>
          {detail ? (
            <div className="min-w-0 space-y-5 overflow-x-hidden overflow-y-auto p-6 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 break-words text-muted-foreground">
                  {detail.reference} , {new Date(detail.created_at).toLocaleString("de-CH")} ,{" "}
                  {detail.locale.toUpperCase()}
                </div>
                <Button
                  className="shrink-0"
                  size="sm"
                  onClick={async () => {
                    try {
                      const { downloadSubmissionPdf } = await import("@/lib/submission-pdf");
                      downloadSubmissionPdf(detail, {
                        formLabel: FORM_LABELS[detail.form_type] ?? detail.form_type,
                        statusLabel: STATUS_LABELS[detail.status] ?? detail.status,
                      });
                    } catch {
                      toast.error("PDF konnte nicht erstellt werden.");
                    }
                  }}
                >
                  <FileText className="size-4" /> PDF erstellen
                </Button>
              </div>

              {detail.form_type === "off_market" && <OffMarketDocuments id={detail.id} />}

              <SubmissionReview
                key={detail.id}
                submission={detail}
                onChanged={async () => {
                  setDetail(null);
                  await load();
                }}
              />

              {(detail.payload ?? []).map((section) => (
                <div key={section.title} className="space-y-2">
                  <h3 className="font-semibold">{section.title}</h3>
                  <dl className="grid gap-1">
                    {section.fields.map((field) => (
                      <div
                        key={field.label}
                        className="grid min-w-0 gap-1 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-2"
                      >
                        <dt className="text-muted-foreground">{field.label}</dt>
                        <dd className="min-w-0 whitespace-pre-wrap break-words">{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubmissionReview({
  submission,
  onChanged,
}: {
  submission: SubmissionRow;
  onChanged: () => void | Promise<void>;
}) {
  const loadReview = useServerFn(getSubmissionReview);
  const approve = useServerFn(approveSubmission);
  const reactivate = useServerFn(reactivateSubmission);
  const reject = useServerFn(rejectSubmission);
  const unpublish = useServerFn(unpublishSubmission);
  const remove = useServerFn(deleteManagedSubmission);

  type Review = Awaited<ReturnType<typeof getSubmissionReview>>;
  const [review, setReview] = useState<Review | null>(null);
  const [loadingReview, setLoadingReview] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<"approved" | "rejected" | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [confirm, setConfirm] = useState<
    "normal" | "force" | "reactivate" | "reactivate_force" | null
  >(null);

  const refresh = useMemo(
    () => async () => {
      setLoadingReview(true);
      try {
        setReview(await loadReview({ data: { id: submission.id } }));
      } catch {
        toast.error("Prüfdaten konnten nicht geladen werden.");
      } finally {
        setLoadingReview(false);
      }
    },
    [loadReview, submission.id],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      await onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Aktion fehlgeschlagen.");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (loadingReview && !review) {
    return <p className="text-muted-foreground">Prüfung wird geladen.</p>;
  }
  if (!review) return null;

  const {
    validation,
    auditLog,
    emails,
    published,
    entity,
    previewEntity,
    formType,
    billingInvoice,
  } = review;
  const propertyAwaitingPayment =
    formType === "immobilie" &&
    Boolean(billingInvoice) &&
    (billingInvoice?.status === "open" || billingInvoice?.status === "overdue");
  const publishable = validation.target !== null;
  const reactivationEligible =
    (formType === "registrierung" || formType === "konto_registrierung") &&
    !published &&
    (submission.status === "abgelehnt" ||
      (submission.status === "in_bearbeitung" &&
        (formType === "konto_registrierung" || Boolean(submission.published_id))));
  const confirmingReactivation = confirm === "reactivate" || confirm === "reactivate_force";

  const payloadMap = new Map<string, string>();
  for (const section of submission.payload ?? []) {
    for (const field of section.fields) {
      if (!payloadMap.has(field.label)) payloadMap.set(field.label, field.value);
    }
  }
  const diffRows: {
    label: string;
    before: string;
    after: string;
    state: "new" | "changed" | "same" | "omitted";
  }[] = validation.fields.map((field) => {
    const before = payloadMap.get(field.label);
    return {
      label: field.label,
      before: before ?? "",
      after: field.value,
      state: before === undefined ? "new" : before === field.value ? "same" : "changed",
    };
  });
  const publishedLabels = new Set(validation.fields.map((field) => field.label));
  for (const [label, value] of payloadMap) {
    if (!publishedLabels.has(label)) {
      diffRows.push({ label, before: value, after: "", state: "omitted" });
    }
  }
  const diffBadge = {
    new: { text: "neu", className: "bg-brand-soft text-brand-deep" },
    changed: { text: "geändert", className: "bg-amber-100 text-amber-800" },
    same: { text: "unverändert", className: "bg-muted text-muted-foreground" },
    omitted: { text: "wird nicht publiziert", className: "bg-muted text-muted-foreground" },
  } as const;

  const diffJson = {
    meta: {
      reference: submission.reference,
      formType: submission.form_type,
      headline: submission.headline,
      customerEmail: submission.customer_email,
      createdAt: submission.created_at,
      generatedAt: new Date().toISOString(),
    },
    draft: Object.fromEntries(payloadMap),
    published: Object.fromEntries(validation.fields.map((field) => [field.label, field.value])),
    diff: diffRows,
  };

  const copyDiffJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diffJson, null, 2));
      toast.success("Diff-JSON in Zwischenablage kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const downloadDiffJson = () => {
    const blob = new Blob([JSON.stringify(diffJson, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `begner-diff-${submission.reference}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {publishable ? (
        <div className="space-y-4 rounded-xl border border-border bg-muted/40 p-4">
          <div>
            <h3 className="font-semibold">
              {formType === "konto_registrierung"
                ? "Prüfung vor Kontofreigabe"
                : "Prüfung vor Veröffentlichung"}
            </h3>
            {validation.errors.length === 0 ? (
              <p className="mt-1 text-muted-foreground">
                Alle Pflichtfelder sind vorhanden und plausibel.
              </p>
            ) : (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-destructive">
                {validation.errors.map((issue) => (
                  <li key={`${issue.field}-${issue.message}`}>
                    <span className="font-medium">{issue.field}:</span> {issue.message}
                  </li>
                ))}
              </ul>
            )}
            {validation.warnings.length > 0 ? (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                {validation.warnings.map((issue) => (
                  <li key={`${issue.field}-${issue.message}`}>
                    <span className="font-medium">{issue.field}:</span> {issue.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {propertyAwaitingPayment ? (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-950">
              <p className="font-semibold">Geprüft – Zahlung noch offen</p>
              <p className="mt-1 text-sm leading-6 text-blue-900/80">
                Die Rechnung {billingInvoice?.invoiceNumber} ist ausgestellt. Das Inserat bleibt
                verborgen und wird automatisch freigeschaltet, sobald der Zahlungseingang im
                Rechnungsbereich bestätigt wird.
              </p>
              <Button asChild size="sm" variant="outline" className="mt-3 bg-white">
                <Link to="/admin-rechnungen">Rechnung öffnen</Link>
              </Button>
            </div>
          ) : null}

          {validation.similar.length > 0 ? (
            <div>
              <h4 className="font-semibold">Mögliche Duplikate</h4>
              <ul className="mt-1 space-y-1 text-muted-foreground">
                {validation.similar.map((item) => (
                  <li key={item.id}>
                    {item.label} , {item.location} , {item.status} ,{" "}
                    {new Date(item.created_at).toLocaleDateString("de-CH")}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Freigabe wird bei Duplikaten erst nach Bestätigung ausgeführt.
              </p>
            </div>
          ) : null}

          {formType === "auftrag" && entity ? (
            <JobDraftEditor
              submissionId={submission.id}
              entity={entity}
              previewImages={previewEntity?.images ?? []}
              disabled={published || busy}
              onSaved={refresh}
            />
          ) : null}

          {formType !== "auftrag" && entity ? (
            <EntityDraftEditor
              key={`${submission.id}-${JSON.stringify(entity)}`}
              submissionId={submission.id}
              formType={formType}
              entity={entity as unknown as Record<string, unknown>}
              disabled={
                propertyAwaitingPayment || (published && formType !== "konto_registrierung")
              }
              onSaved={refresh}
            />
          ) : null}

          <div>
            <h4 className="font-semibold">
              {formType === "konto_registrierung"
                ? "Diese Kontodaten werden geprüft"
                : "Diese Felder werden veröffentlicht"}
            </h4>
            <dl className="mt-1 grid gap-1">
              {validation.fields.map((field) => (
                <div
                  key={field.label}
                  className="grid min-w-0 gap-1 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-2"
                >
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="min-w-0 whitespace-pre-wrap break-words">{field.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="space-y-2">
            <Label htmlFor="decision-reason">
              Hinweis an die Absenderin oder den Absender (optional)
            </Label>
            <Textarea
              id="decision-reason"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Wird in der Entscheidungs-E-Mail mitgeschickt."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {reactivationEligible ? (
              <Button
                size="sm"
                disabled={busy || !validation.canPublish}
                onClick={() => setConfirm("reactivate")}
              >
                <RotateCcw className="size-4" /> Kunde reaktivieren
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={published || propertyAwaitingPayment || busy || !validation.canPublish}
                onClick={() => setConfirm("normal")}
              >
                {formType === "immobilie" ? "Prüfen & Rechnung ausstellen" : "Akzeptieren"}
              </Button>
            )}
            {validation.similar.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={published || propertyAwaitingPayment || busy || !validation.canPublish}
                onClick={() => setConfirm(reactivationEligible ? "reactivate_force" : "force")}
              >
                {reactivationEligible ? "Trotz Duplikat reaktivieren" : "Trotzdem freigeben"}
              </Button>
            ) : null}

            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                run(
                  () => reject({ data: { id: submission.id, reason: reason.trim() || undefined } }),
                  "Abgelehnt, nichts veröffentlicht, E-Mail versendet.",
                )
              }
            >
              Ablehnen
            </Button>
            {published ? (
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      unpublish({
                        data: { id: submission.id, reason: reason.trim() || undefined },
                      }),
                    formType === "konto_registrierung"
                      ? "Konto pausiert. Mitgliedschaft und Kontaktzugriff wurden deaktiviert."
                      : "Eintrag wurde aus den öffentlichen Listen entfernt.",
                  )
                }
              >
                Rückgängig, offline nehmen
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={() => setDeleteConfirm(true)}
            >
              <Trash2 className="size-4" /> Dauerhaft löschen
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPreview(preview === "approved" ? null : "approved")}
              >
                Vorschau Freigabe-E-Mail
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPreview(preview === "rejected" ? null : "rejected")}
              >
                Vorschau Absage-E-Mail
              </Button>
            </div>
            {preview ? (
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="font-medium">{emails[preview].subject}</p>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-muted-foreground">
                  {emails[preview].text}
                  {reason.trim() ? `\n\nHinweis: ${reason.trim()}` : ""}
                </pre>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div>
        <h3 className="font-semibold">Auditlog</h3>
        {auditLog.length === 0 ? (
          <p className="mt-1 text-muted-foreground">Noch keine Entscheidungen protokolliert.</p>
        ) : (
          <ul className="mt-2 space-y-3">
            {auditLog.map((entry) => (
              <li key={entry.id} className="rounded-lg border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{entry.action}</Badge>
                  <span className="text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString("de-CH")} , {entry.actor}
                  </span>
                  {entry.email_status ? (
                    <span className="text-xs text-muted-foreground">
                      E-Mail: {entry.email_status}
                    </span>
                  ) : null}
                </div>
                {entry.reason ? <p className="mt-1">Hinweis: {entry.reason}</p> : null}
                {entry.published_fields ? (
                  <dl className="mt-2 grid gap-1 text-xs">
                    {Object.entries(entry.published_fields).map(([label, value]) => (
                      <div
                        key={label}
                        className="grid min-w-0 gap-1 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-2"
                      >
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="min-w-0 break-words">{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Separator />

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {confirmingReactivation
                ? "Kunde reaktivieren"
                : formType === "konto_registrierung"
                  ? "Kontofreigabe bestätigen"
                  : "Vorschau der Veröffentlichung"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirmingReactivation
              ? formType === "konto_registrierung"
                ? "Das Anbieterkonto wird erneut freigeschaltet. Eine inaktive Mitgliedschaft wird dabei wieder auf aktiv gesetzt."
                : "Der Anbieter wird wieder veröffentlicht und das zugehörige, typgleiche Anbieterkonto erneut freigeschaltet. Die bestehende Mitgliedschaft bleibt unverändert."
              : formType === "konto_registrierung"
                ? "Das bestätigte Firmenkonto wird geprüft freigeschaltet. Ein bezahlter Tarif ermöglicht Interessenbekundungen. Kontaktdaten werden erst nach der ausdrücklichen Auswahl durch den Auftraggeber freigegeben."
                : formType === "immobilie"
                  ? "Das Inserat wird geprüft und die gewählte QR-Rechnung ausgestellt. Es bleibt bis zur bestätigten Zahlung verborgen; die gebuchte Laufzeit startet erst mit der Freischaltung."
                  : "So erscheint der Eintrag öffentlich, inklusive Vergleich der eingereichten Angaben."}
          </p>
          <Tabs defaultValue="page" className="mt-3">
            <TabsList>
              <TabsTrigger value="page">Detailseite</TabsTrigger>
              <TabsTrigger value="diff">Feld-Diff</TabsTrigger>
            </TabsList>
            <TabsContent value="page" className="mt-3">
              {formType === "konto_registrierung" ? (
                <div className="rounded-xl border border-border bg-muted/30 p-5">
                  <h3 className="font-semibold">
                    {String(
                      (entity as unknown as Record<string, unknown> | null)?.["company_name"] ??
                        submission.headline,
                    )}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{submission.customer_email}</p>
                  <Badge className="mt-3" variant="secondary">
                    Kontodaten noch nicht öffentlich
                  </Badge>
                </div>
              ) : (
                <PublishPreview formType={formType} entity={previewEntity} />
              )}
            </TabsContent>
            <TabsContent value="diff" className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={copyDiffJson}>
                  <Copy className="size-4" /> JSON kopieren
                </Button>
                <Button size="sm" variant="outline" onClick={downloadDiffJson}>
                  <FileJson className="size-4" /> JSON herunterladen
                </Button>
              </div>
              <div className="space-y-2">
                {diffRows.map((row) => (
                  <div
                    key={`${row.label}-${row.state}`}
                    className="grid gap-1 rounded-lg border border-border p-3 text-sm md:grid-cols-[180px_1fr_1fr]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{row.label}</span>
                    </div>
                    <div className="text-muted-foreground line-through decoration-muted-foreground/50 empty:hidden">
                      {row.state === "same" || row.state === "new" ? "" : row.before}
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <span className="whitespace-pre-wrap break-words">
                        {row.state === "omitted" ? "" : row.after}
                      </span>
                      <Badge
                        variant="secondary"
                        className={`shrink-0 ${diffBadge[row.state].className}`}
                      >
                        {diffBadge[row.state].text}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                const selectedAction = confirm;
                setConfirm(null);
                if (selectedAction === "reactivate" || selectedAction === "reactivate_force") {
                  void run(
                    () =>
                      reactivate({
                        data: {
                          id: submission.id,
                          reason: reason.trim() || undefined,
                          force: selectedAction === "reactivate_force" ? true : undefined,
                        },
                      }),
                    "Kunde reaktiviert. Anbieter und Kontozugriff wurden synchron aktualisiert.",
                  );
                  return;
                }
                const force = selectedAction === "force";
                void run(
                  () =>
                    approve({
                      data: {
                        id: submission.id,
                        reason: reason.trim() || undefined,
                        force: force ? true : validation.similar.length > 0 ? false : undefined,
                      },
                    }),
                  force
                    ? formType === "immobilie"
                      ? "Trotz Duplikat geprüft; Rechnung ausgestellt."
                      : "Trotz Duplikat freigegeben und veröffentlicht."
                    : formType === "immobilie"
                      ? "Geprüft; Rechnung ausgestellt. Freischaltung folgt nach Zahlung."
                      : "Freigegeben, veröffentlicht und E-Mail versendet.",
                );
              }}
            >
              {confirmingReactivation
                ? "Jetzt reaktivieren"
                : formType === "konto_registrierung"
                  ? "Konto freigeben"
                  : formType === "immobilie"
                    ? "Prüfen & Rechnung ausstellen"
                    : "Jetzt veröffentlichen"}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirm(null)}>
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Eintrag dauerhaft löschen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-6 text-muted-foreground">
            {formType === "konto_registrierung"
              ? "Das Supabase-Login, das Anbieterprofil und diese Admin-Anfrage werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."
              : "Die Anfrage und ein zugehöriger öffentlicher Eintrag werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(false)} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                run(
                  () =>
                    remove({
                      data: { id: submission.id, reason: reason.trim() || undefined },
                    }),
                  "Eintrag wurde dauerhaft gelöscht.",
                )
              }
            >
              <Trash2 className="size-4" /> Endgültig löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OffMarketDocuments({ id }: { id: string }) {
  const load = useServerFn(getOffMarketDocuments);
  const [documents, setDocuments] = useState<{ name: string; url: string }[]>([]);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3 rounded-xl border border-border p-4">
      <p className="font-semibold">Vertrauliche Unterlagen</p>
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const files = await load({ data: { id } });
            setDocuments(files);
            if (!files.length) toast.info("Keine Unterlagen hochgeladen.");
          } catch {
            toast.error("Unterlagen konnten nicht geladen werden.");
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Laden …" : "Geschützte Downloadlinks laden"}
      </Button>
      <p className="text-xs text-muted-foreground">
        Links sind 60 Sekunden gültig. Bei Bedarf erneut laden.
      </p>
      {documents.map((file, index) => (
        <a
          key={index}
          href={file.url}
          target="_blank"
          rel="noreferrer"
          className="block break-words text-brand-deep underline"
        >
          {file.name}
        </a>
      ))}
    </div>
  );
}
